import iconSvg from '!!raw-loader!../../../public_html/img/common/map-logo.svg';

/**
 * グーグルマップと検索の操作を行うクラス
 */
export class MapOperation {
    constructor(props) {
        this.props = props;
        this.debug = Boolean(props.d ?? false);
        this.suspendLevel = 1;
        this.eventSuspended = false;
        this.counter = 0;
        this.results = {};

        // 最後に検索Ajaxを呼び出した時のunix timestamp。秒1回のレート制限に使用する
        this.lastSearchTimestamp = 0;
        this.minimumSearchInterval = 1000; // ミリ秒

        // see: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
        this.map = new google.maps.Map(document.getElementById('map'), {
            clickableIcons: false,
            fullscreenControl: false,
            mapTypeControl: false,
            panControl: false,
            rotateControl: false,
            scaleControl: true,
            streetViewControl: false,
            zoom: props.zoom,
        });

        this.infoWindow = new google.maps.InfoWindow();
        this.infoWindowMarker = null;

        // svgファイルのパスを指定する形だと重くなるという情報があったのでraw-loaderで読み取ったsvgのソースをdata:の形で埋め込む
        // see: https://qiita.com/aoi_/items/7c70d725b4fa9321a740
        this.markerIcon = {
            url: 'data:image/svg+xml;charset=utf8,' + encodeURIComponent(iconSvg),
            scaledSize: new google.maps.Size(27, 27),
            anchor: new google.maps.Point(13, 13),
        };
    }

    initMap() {
        this.initCenter();
        this.map.addListener('idle', () => this.changeHandler());
        this.infoWindow.addListener('closeclick', () => this.infoWindowClosed());
        this.infoWindowClosed();
        this.resume();
    }

    infoWindowClosed() {
        this.infoWindowMarker = null;
        if (this.debug) {
            $('#db-iw').text('close');
        }
    }

    initCenter() {
        // 座標の指定があればそれを使用する
        if (this.props.lat !== null && this.props.lng !== null) {
            this.setCenter(this.props.lat, this.props.lng);
            return;
        }

        // 座標の指定が無い場合は現在位置を取得して使用する(取得できない場合は東京駅の座標)
        this.goToCurrentLocation(true);
    }

    setCenter(lat, lng) {
        this.map.setCenter(new google.maps.LatLng(lat, lng));
    }

    changeHandler() {
        if (this.suspendLevel > 0) {
            this.eventSuspended = true;
        } else {
            this.search();
        }
    }

    suspend() {
        this.suspendLevel += 1;

        if (this.debug) {
            $('#db-stat').text('suspended');
        }
    }

    resume() {
        if (this.suspendLevel > 0) {
            this.suspendLevel -= 1;
        }

        if (this.suspendLevel === 0) {
            if (this.eventSuspended) {
                this.eventSuspended = false;
                this.search();
            }

            if (this.debug) {
                $('#db-stat').text('running');
            }
        }
    }

    search() {
        const bounds = this.map.getBounds();
        const center = this.map.getCenter();
        const zoom = this.map.getZoom();

        if (!bounds || !center || !zoom) {
            return;
        }

        const boundsJson = bounds.toJSON();
        const condition = {
            lat: center.lat(),
            lng: center.lng(),
            z: zoom,
            rp: $('#select-recruiting-personnel').val(),
            ph: $('#select-pay-per-hour').val(),
            wf: $('#input-working-date-from').val(),
            wt: $('#input-working-date-to').val(),
            tc: $('#select-travel-cost').val(),
            sp: $('#select-staff-parking').val(),
            dw:  $('input[name="dw[]"]:checked').map((i, e) => e.value).get(),
            ...boundsJson, // 'west', 'south', 'east', 'north' が展開される
        };

        if (this.debug) {
            this.counter += 1;
            $('#db-counter').text(this.counter);
            $('#db-ct').text(center);
            $('#db-sw').text(bounds.getSouthWest());
            $('#db-ne').text(bounds.getNorthEast());
            $('#db-z').text(zoom);
            $('#db-need').text(zoom < this.props.m ? '[need zoom]' : '');
        }

        if (zoom < this.props.m) {
            // 最小ズームより低ければ検索を行わない
            // 「拡大してください」を表示
            this.showNeedZoom();
            this.clearAllMarkers();
            return;
        } else {
            this.hideNeedZoom();
        }

        // 秒1回にレート制限
        const nowTimestamp = new Date().getTime();
        if (nowTimestamp - this.lastSearchTimestamp < this.minimumSearchInterval) {
            return;
        }

        // 検索実行
        this.lastSearchTimestamp = nowTimestamp;
        this.suspend();
        $.ajax({
            type: 'POST',
            url: this.props.url,
            data: JSON.stringify(condition),
            contentType: 'application/json; charset=utf-8',
            dataType: 'json',
            cache: false,
            processData: false,
            headers: {
                'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
            },
        }).then(
            (data) => {
                // 取得成功
                try {
                    if (data.status === 'error') {
                        // API内でエラー発生
                        this.clearAllMarkers();
                        alert(data.message);
                        return;
                    }

                    if (this.debug) {
                        $('#db-result').text(data.job_offer_count);
                    }

                    if (data?.need_zoom) {
                        this.showNeedZoom();
                        this.clearAllMarkers();
                        return;
                    } else {
                        this.hideNeedZoom();
                    }

                    this.adjustMarkers(data.results);
                } finally {
                    this.resume();
                }
            },
            () => {
                // エラー発生
                this.clearAllMarkers();
                alert('データを取得できませんでした。');
                this.resume();
            }
        );
    }

    showNeedZoom() {
        // 「拡大してください」を表示
        $('#div-need-zoom').show();
        if (this.debug) {
            $('#db-need').text('[need zoom]');
        }
    }

    hideNeedZoom() {
        $('#div-need-zoom').hide();
        if (this.debug) {
            $('#db-need').empty();
        }
    }

    clearAllMarkers() {
        const prevResults = this.results;
        this.results = {};

        const prevResultsKeys = Object.keys(prevResults);
        prevResultsKeys.forEach((removingKey) => this.clearMarker(prevResults[removingKey].marker));
    }

    clearMarker(marker) {
        if (marker === this.infoWindowMarker) {
            this.infoWindow.close();
            this.infoWindowClosed();
        }
        marker.setMap(null);
    }

    adjustMarkers(results) {
        const prevResults = this.results;
        this.results = results;

        // prevResultsにあってresultsに無いマーカーを削除する
        const prevResultsKeys = Object.keys(prevResults);
        const resultsKeys = Object.keys(results);
        const removingKeys = prevResultsKeys.filter((v) => resultsKeys.indexOf(v) < 0);
        removingKeys.forEach((removingKey) => this.clearMarker(prevResults[removingKey].marker));

        // resultsにマーカーとコンテンツをセットする
        resultsKeys.forEach((resultKey) => {
            const result = results[resultKey];
            let marker;
            // prevResultsにあるなら再利用する
            if (prevResults[resultKey]?.marker) {
                marker = prevResults[resultKey].marker;
                google.maps.event.clearListeners(marker, 'click');
            } else {
                marker = new google.maps.Marker();
            }
            marker.setOptions({
                position: { lat: result.latitude, lng: result.longitude },
                map: this.map,
                title: result.shop_name,
                icon: this.markerIcon,
            });
            marker.addListener('click', () => {
                this.infoWindowMarker = marker;
                this.infoWindow.setContent(result.content);
                this.infoWindow.open({
                    anchor: marker,
                    map: this.map,
                });
                if (this.debug) {
                    $('#db-iw').text('open');
                }
            });

            result.marker = marker;

            // 吹き出しを開いているならコンテンツを更新する
            if (this.infoWindowMarker === marker) {
                this.infoWindow.setContent(result.content);
            }
        });
    }

    goToCurrentLocation(defaultLocation = false) {
        navigator.geolocation.getCurrentPosition(
            // success
            (position) => {
                this.setCenter(position.coords.latitude, position.coords.longitude);
            },
            // fail
            (error) => {
                alert('位置情報の取得に失敗しました。エラーコード：' + error.code);
                if (defaultLocation) {
                    // 東京駅
                    this.setCenter(35.6812405, 139.7649361);
                }
            }
        );
    }

    saveCondition(conditionNumber) {
        if (conditionNumber < 1 || conditionNumber > 3) {
            alert('保存できませんでした。');
            return;
        }
        const parameters = {
            sc: conditionNumber,
            rp: $('#select-recruiting-personnel').val(),
            ph: $('#select-pay-per-hour').val(),
            wf: $('#input-working-date-from').val(),
            wt: $('#input-working-date-to').val(),
            tc: $('#select-travel-cost').val(),
            sp: $('#select-staff-parking').val(),
            dw: $('input[name="dw[]"]:checked').map((i, e) => e.value).get(),
        };
        const modalId = 'modal-save-conditions';

        // 絞り込み条件を保存
        this.suspend();
        $.ajax({
            type: 'POST',
            url: this.props.save,
            data: JSON.stringify(parameters),
            contentType: 'application/json; charset=utf-8',
            dataType: 'json',
            cache: false,
            processData: false,
            headers: {
                'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
            },
        }).then(
            (data) => {
                // API呼び出し成功
                try {
                    if (data.status === 'error') {
                        // API内でエラー発生
                        alert(data.message);
                        return;
                    }
                } finally {
                    MicroModal.close(modalId);
                    this.resume();
                }
            },
            () => {
                // エラー発生
                alert('保存できませんでした。');
                MicroModal.close(modalId);
                this.resume();
            }
        );
    }

    loadCondition(conditionNumber) {
        if (conditionNumber < 1 || conditionNumber > 3) {
            alert('呼び出しできませんでした。');
            return;
        }
        const parameters = {
            sc: conditionNumber,
        };
        const modalId = 'modal-load-conditions';

        // 絞り込み条件を呼び出す
        this.suspend();
        $.ajax({
            type: 'POST',
            url: this.props.load,
            data: JSON.stringify(parameters),
            contentType: 'application/json; charset=utf-8',
            dataType: 'json',
            cache: false,
            processData: false,
            headers: {
                'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
            },
        }).then(
            (data) => {
                // API呼び出し成功
                try {
                    if (data.status === 'error') {
                        // API内でエラー発生
                        alert(data.message);
                        return;
                    }

                    this.applyConditions(data.conditions);
                } finally {
                    MicroModal.close(modalId);
                    this.resume();
                }
            },
            () => {
                // エラー発生
                alert('呼び出しできませんでした。');
                MicroModal.close(modalId);
                this.resume();
            }
        );
    }

    applyConditions(conditions) {
        $('#select-recruiting-personnel').val(conditions?.rp).trigger('change');
        $('#select-pay-per-hour').val(conditions?.ph).trigger('change');
        $('#input-working-date-from').val(conditions?.wf).trigger('change');
        $('#input-working-date-to').val(conditions?.wt).trigger('change');
        $('#select-travel-cost').val(conditions?.tc).trigger('change');
        $('#select-staff-parking').val(conditions?.sp).trigger('change');
        $('input[name="dw[]"]').val(conditions?.dw).trigger('change');
    }

    inquireAvailableNumbers() {
        const parameters = {};

        // 使用可能な番号を問い合わせる
        this.suspend();
        $.ajax({
            type: 'POST',
            url: this.props.inquire,
            data: JSON.stringify(parameters),
            contentType: 'application/json; charset=utf-8',
            dataType: 'json',
            cache: false,
            processData: false,
            headers: {
                'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
            },
        }).then(
            (data) => {
                // API呼び出し成功
                try {
                    if (data.status === 'error') {
                        // API内でエラー発生
                        alert(data.message);
                        return;
                    }

                    this.applySearchNumbers(data.available);
                } finally {
                    this.resume();
                }
            },
            () => {
                // エラー発生
                alert('呼び出しできませんでした。');
                this.resume();
            }
        );
    }

    applySearchNumbers(available) {
        // ボタンのdisabled属性をavailable配列に含まれているかどうかで設定する
        [1, 2, 3].forEach((conditionNumber) => {
            const buttonSelector = '#condition-button-' + conditionNumber;
            $('#condition-button-' + conditionNumber).prop(
                'disabled',
                available.indexOf(conditionNumber) < 0
            );
        });
    }
}
